import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Typography } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import {
  format,
  addDays,
  addHours,
  addMonths,
  isToday,
  isTomorrow,
  isSameDay,
  startOfDay,
  endOfDay,
  getMonth,
  getYear,
  isAfter,
} from 'date-fns';
import {
  BUTTON_PRIMARY,
  BUTTON_WARNING,
  DATE_TIME_SELECTOR_DATE_OPTIONS,
  DATE_TIME_SELECTOR_DEFAULT_LOCKER_PICKUP_GAP_HOURS,
  DATE_TIME_SELECTOR_MAX_BOOKING_WINDOW_MONTHS,
  DATE_TIME_SELECTOR_MAX_DURATION_HOURS,
  DATE_TIME_SELECTOR_MONTHS,
  DATE_TIME_SELECTOR_WEEKDAYS,
  POD_BANK_TYPE,
  TOAST_ERROR
} from '../assets/constants/Constants';
import {
  add30Minutes,
  findMatchingTimeSlot,
  formatDisplayDate,
  formatToISOString,
  generateTimeSlots,
  parseTimeToDate,
} from '../lib/Utils';
import CustomDialog from './CustomDialog';
import ShowToast from './ToastComponent';

function Calendar({ onSelect, onClose, minDate = new Date(), maxDate = null, selectedDate }) {
  const today = new Date();
  const [viewDate, setViewDate] = useState(selectedDate || today);
  const [picked, setPicked] = useState(null);

  const viewYear = getYear(viewDate);
  const viewMonth = getMonth(viewDate);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const minBoundary = startOfDay(minDate);
  const maxBoundary = maxDate ? endOfDay(maxDate) : null;

  const isMonthBeforeMin = () => {
    const previousMonthLastDate = new Date(viewYear, viewMonth, 0);
    return previousMonthLastDate < minBoundary;
  };

  const isMonthAfterMax = () => {
    if (!maxBoundary) return false;
    const nextMonthFirstDate = new Date(viewYear, viewMonth + 1, 1);
    return nextMonthFirstDate > maxBoundary;
  };

  const prevMonth = () => {
    if (isMonthBeforeMin()) return;
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setPicked(null);
  };

  const nextMonth = () => {
    if (isMonthAfterMax()) return;
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setPicked(null);
  };

  const isDatePast = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    const isBeforeMinDate = date < minBoundary && !isSameDay(date, minBoundary);
    const isAfterMaxDate = maxBoundary ? date > maxBoundary && !isSameDay(date, maxBoundary) : false;
    return isBeforeMinDate || isAfterMaxDate;
  };

  const isDateToday = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    return isSameDay(date, today);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const handleConfirm = () => {
    if (!picked) return;
    const selectedDate = new Date(viewYear, viewMonth, picked);
    onSelect(selectedDate, format(selectedDate, 'MMM d'));
    onClose();
  };

  return (
    <div className="calendar-card">
        <div className="calendar-header">
          <button className="calendar-nav" onClick={prevMonth}>‹</button>
          <span className="calendar-month">{DATE_TIME_SELECTOR_MONTHS[viewMonth]} {viewYear}</span>
          <button className="calendar-nav" onClick={nextMonth}>›</button>
        </div>

        <div className="calendar-weekdays">
          {DATE_TIME_SELECTOR_WEEKDAYS.map(d => (
            <div key={d} className="calendar-weekday">{d}</div>
          ))}
        </div>

        <div className="calendar-days">
          {cells.map((day, i) => {
            if (!day) {
              return <div key={`e-${i}`} className="calendar-day empty" />;
            }
            
            const past = isDatePast(day);
            const sel = picked === day;
            const tod = isDateToday(day);
            
            let cls = "calendar-day";
            if (past) cls += " past";
            else if (sel) cls += " selected";
            else if (tod) cls += " today";
            
            return (
              <button 
                key={day} 
                className={cls} 
                onClick={() => !past && setPicked(day)}
                disabled={past}
              >
                {day}
              </button>
            );
          })}
        </div>

        <button 
          className="calendar-confirm" 
          onClick={handleConfirm} 
          disabled={!picked}
        >
          {picked
            ? `Confirm — ${DATE_TIME_SELECTOR_MONTHS[viewMonth].slice(0, 3)} ${picked}`
            : "Select a day"}
        </button>
    </div>
  );
}

const DateTimeSelector = ({ onContinue, lockerType }) => {
  const { t } = useTranslation();
  const isPodLockerType = String(lockerType).toLowerCase() === POD_BANK_TYPE;
  const [activeSection, setActiveSection] = useState("dropoff");
  const [bookingWindowStart] = useState(() => new Date());
  const [bookingWindowEnd] = useState(() => addMonths(new Date(), DATE_TIME_SELECTOR_MAX_BOOKING_WINDOW_MONTHS));

  const getMaxPickupDateTime = useCallback((dropoffDateTime) => {
    if (!isPodLockerType) {
      return bookingWindowEnd;
    }
    const maxByDuration = addHours(dropoffDateTime, DATE_TIME_SELECTOR_MAX_DURATION_HOURS);
    return isAfter(maxByDuration, bookingWindowEnd) ? bookingWindowEnd : maxByDuration;
  }, [bookingWindowEnd, isPodLockerType]);

  const getDefaultPickupDateTime = useCallback((dropoffDateTime) => (
    isPodLockerType
      ? add30Minutes(dropoffDateTime)
      : addHours(dropoffDateTime, DATE_TIME_SELECTOR_DEFAULT_LOCKER_PICKUP_GAP_HOURS)
  ), [isPodLockerType]);

  // Dropoff state
  const [dropoffDate, setDropoffDate] = useState(new Date());
  const [dropoffTime, setDropoffTime] = useState("");
  const [dropoffCustomLabel, setDropoffCustomLabel] = useState(null);
  const [showDropoffCalendar, setShowDropoffCalendar] = useState(false);
  const [dropoffTimeSlots, setDropoffTimeSlots] = useState([]);
  
  // Pickup state
  const [pickupDate, setPickupDate] = useState(new Date());
  const [pickupTime, setPickupTime] = useState("");
  const [pickupCustomLabel, setPickupCustomLabel] = useState(null);
  const [showPickupCalendar, setShowPickupCalendar] = useState(false);
  const [pickupTimeSlots, setPickupTimeSlots] = useState([]);
  const[confirmationModel,setConfirmationModel] = useState(false);
  const [pendingDateTimeData, setPendingDateTimeData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dropoffDateTime = parseTimeToDate(dropoffDate, dropoffTime);
  const pickupWindowEnd = dropoffDateTime ? getMaxPickupDateTime(dropoffDateTime) : bookingWindowEnd;

  // Initialize with current time
  useEffect(() => {
    const now = bookingWindowStart;
    setDropoffDate(now);
    
    const slots = generateTimeSlots(now, now, bookingWindowEnd, isPodLockerType);
    setDropoffTimeSlots(slots);
    setDropoffTime(slots[0]);

    const dropoffMoment = parseTimeToDate(now, slots[0]) || now;
    const maxPickupDateTime = getMaxPickupDateTime(dropoffMoment);
    const defaultPickupDateTime = getDefaultPickupDateTime(dropoffMoment);
    const pickupDateTime = isAfter(defaultPickupDateTime, maxPickupDateTime)
      ? maxPickupDateTime
      : defaultPickupDateTime;
    setPickupDate(pickupDateTime);


    const pickupSlots = generateTimeSlots(pickupDateTime, pickupDateTime, maxPickupDateTime, isPodLockerType);
    setPickupTimeSlots(pickupSlots);
    setPickupTime(pickupSlots[0]);
  }, [bookingWindowEnd, bookingWindowStart, getMaxPickupDateTime, getDefaultPickupDateTime, isPodLockerType]);

  // Update pickup when dropoff changes
  useEffect(() => {
    if (dropoffDate && dropoffTime) {
      const dropoffDateTime = parseTimeToDate(dropoffDate, dropoffTime);
      if (dropoffDateTime) {
        const maxPickupDateTime = getMaxPickupDateTime(dropoffDateTime);
        const computedPickupDate = getDefaultPickupDateTime(dropoffDateTime);
        const newPickupDate = isAfter(computedPickupDate, maxPickupDateTime)
          ? maxPickupDateTime
          : computedPickupDate;
        setPickupDate(newPickupDate);

        // Generate new pickup time slots, capped to the max booking duration after dropoff
        const slots = generateTimeSlots(newPickupDate, newPickupDate, maxPickupDateTime, isPodLockerType);
        setPickupTimeSlots(slots);

        const matchingSlot = findMatchingTimeSlot(slots, newPickupDate);

        setPickupTime(matchingSlot || slots[0]);
      }
    }
  }, [dropoffDate, dropoffTime, getMaxPickupDateTime, getDefaultPickupDateTime, isPodLockerType]);

  const handleDropoffDatePill = (value) => {
    if (value === "today") {
      const today = new Date();
      setDropoffDate(today);
      setDropoffCustomLabel(null);
      setShowDropoffCalendar(false);
      
      const slots = generateTimeSlots(today, today, bookingWindowEnd, isPodLockerType);
      setDropoffTimeSlots(slots);
      if (slots.length > 0) {
        setDropoffTime(slots[0]);
      }
    } else if (value === "tomorrow") {
      const tomorrow = addDays(new Date(), 1);
      setDropoffDate(tomorrow);
      setDropoffCustomLabel(null);
      setShowDropoffCalendar(false);
      const slots = generateTimeSlots(tomorrow, tomorrow, bookingWindowEnd, isPodLockerType);
      setDropoffTimeSlots(slots);
      if (slots.length > 0) {
        setDropoffTime(slots[0]); 
      }
    } else if (value === "custom") {
      setShowDropoffCalendar(true);
    }
  };

  const handlePickupDatePill = (value) => {
    if (value === "today") {
      const today = new Date();
      setPickupDate(today);
      setPickupCustomLabel(null);
      setShowPickupCalendar(false);
      const pickupStartTime = isSameDay(today, dropoffDate) && dropoffTime
        ? parseTimeToDate(dropoffDate, dropoffTime) || today
        : today;
      const slots = generateTimeSlots(today, pickupStartTime, pickupWindowEnd, isPodLockerType);
      setPickupTimeSlots(slots);
      if (slots.length > 0) {
        setPickupTime(slots[0]);
      }
    } else if (value === "tomorrow") {
      const tomorrow = addDays(new Date(), 1);
      setPickupDate(tomorrow);
      setPickupCustomLabel(null);
      setShowPickupCalendar(false);
      const slots = generateTimeSlots(tomorrow, tomorrow, pickupWindowEnd, isPodLockerType);
      setPickupTimeSlots(slots);
      if (slots.length > 0) {
        setPickupTime(slots[0]); 
      }
    } else if (value === "custom") {
      setShowPickupCalendar(true);
    }
  };

  const handleDropoffCalendarSelect = (date, label) => {
    setDropoffDate(date);
    setDropoffCustomLabel(label);
    setShowDropoffCalendar(false);
    const slots = generateTimeSlots(date, date, bookingWindowEnd, isPodLockerType);
    setDropoffTimeSlots(slots);
    if (slots.length > 0) {
      setDropoffTime(slots[0]); 
    }
  };

  const handlePickupCalendarSelect = (date, label) => {
    setPickupDate(date);
    setPickupCustomLabel(label);
    setShowPickupCalendar(false);
    const pickupStartTime = isSameDay(date, dropoffDate) && dropoffTime
      ? parseTimeToDate(dropoffDate, dropoffTime) || date
      : date;
    const slots = generateTimeSlots(date, pickupStartTime, pickupWindowEnd, isPodLockerType);
    setPickupTimeSlots(slots);
    if (slots.length > 0) {
      setPickupTime(slots[0]);
    }
  };

  const dropOffDisplay = dropoffDate && dropoffTime
    ? `${formatDisplayDate(dropoffDate)}, ${dropoffTime}`
    : (isPodLockerType ? t('select_start_time_placeholder') : t('select_dropoff_time_placeholder'));
  const pickUpDisplay = pickupDate && pickupTime
    ? `${formatDisplayDate(pickupDate)}, ${pickupTime}`
    : (isPodLockerType ? t('select_end_time_placeholder') : t('select_pickup_time_placeholder'));

  const isPickupPillDisabled = (value) => {
    if (value === "today") {
      const today = new Date();
      const pickupStartTime = isSameDay(today, dropoffDate) && dropoffDateTime ? dropoffDateTime : today;
      return generateTimeSlots(today, pickupStartTime, pickupWindowEnd, isPodLockerType).length === 0;
    }
    if (value === "tomorrow") {
      const tomorrow = addDays(new Date(), 1);
      return generateTimeSlots(tomorrow, tomorrow, pickupWindowEnd, isPodLockerType).length === 0;
    }
    return false;
  };

  const handleCancelBooking = () => {
    if (isSubmitting) {
      return;
    }

    setConfirmationModel(false);
    setPendingDateTimeData(null);
  };

  const handleConfirmBooking = async () => {
    if (!onContinue || !pendingDateTimeData || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const didContinueSucceed = await onContinue(pendingDateTimeData);

      if (didContinueSucceed !== false) {
        setConfirmationModel(false);
        setPendingDateTimeData(null);
      }
    } finally {
        setIsSubmitting(false);
    }
  };

   const handleContinueClick = () => {
     if (isSubmitting) {
      return;
     }
     const reserveStart = formatToISOString(dropoffDate, dropoffTime);
     const reserveEnd = formatToISOString(pickupDate, pickupTime);
     const reserveStartDate = reserveStart ? new Date(reserveStart) : null;
     const reserveEndDate = reserveEnd ? new Date(reserveEnd) : null;

     if (
      (reserveStartDate && isAfter(reserveStartDate, bookingWindowEnd)) ||
      (reserveEndDate && isAfter(reserveEndDate, bookingWindowEnd))
     ) {
      ShowToast(`Please select drop-off and pickup within ${DATE_TIME_SELECTOR_MAX_BOOKING_WINDOW_MONTHS} months from today.`, TOAST_ERROR);
      return;
     }
    
    const dateTimeData = {
      reserve_start: reserveStart,
      reserve_end: reserveEnd
    };
     setPendingDateTimeData(dateTimeData);
     setConfirmationModel(true);
   };

   const renderConfirmationContent = () => {
    return (
      <Box sx={{ pt: 0.5 }}>
        <Typography variant="body2" sx={{ opacity: 0.72, mb: 2 }}>
          {t('warning_msg_confirm_selections_date_time_correct')}
        </Typography>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.5, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <AccessTimeRoundedIcon fontSize="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2">{isPodLockerType ? t('start_time_label') : t('drop_off_label')}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.72 }}>
                {dropOffDisplay}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Box sx={{ p: 1.5, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <EventAvailableRoundedIcon fontSize="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2">{isPodLockerType ? t('end_time_label') : t('pick_up_label')}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.72 }}>
                {pickUpDisplay}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <div className="date-time-selector">
      <div className="phone-shell">
        <div className="scroll-area">
          <div className="summary-card">
            <div 
              className={`summary-row ${activeSection === "dropoff" ? "active" : ""}`} 
              onClick={() => setActiveSection("dropoff")}
              style={{marginLeft:4, borderTopLeftRadius:12, borderBottomLeftRadius:12}}
            >
              <div className="summary-left">
                <div className={`dot ${activeSection === "dropoff" ? "active" : ""}`} />
                <span className="summary-label">{isPodLockerType ? t('start_time_label') : t('drop_off_label')}</span>
              </div>
              <span className="summary-value">{dropOffDisplay}</span>
            </div>
            
            <div 
              className={`summary-row ${activeSection === "pickup" ? "active" : ""}`} 
              onClick={() => setActiveSection("pickup")}
              style={{marginRight:4, borderTopRightRadius:12, borderBottomRightRadius:12}}
            >
              <div className="summary-left">
                <div className={`dot ${activeSection === "pickup" ? "active" : ""}`} />
                <span className="summary-label">{isPodLockerType ? t('end_time_label') : t('pick_up_label')}</span>
              </div>
              <span className="summary-value">{pickUpDisplay}</span>
            </div>
          </div>

          {/* Dropoff Section */}
          {activeSection === "dropoff" && (
            <>
              <div className="section-label">{isPodLockerType ? t('start_date_label') : t('dropoff_date_label')}</div>
              <div className="date-row">
                {DATE_TIME_SELECTOR_DATE_OPTIONS.map((d) => {
                  let isSelected = false;
                  
                  if (d.value === "today") {
                    isSelected = isToday(dropoffDate);
                  } else if (d.value === "tomorrow") {
                    isSelected = isTomorrow(dropoffDate);
                  } else if (d.value === "custom") {
                    isSelected = dropoffCustomLabel !== null && !isToday(dropoffDate) && !isTomorrow(dropoffDate);
                  }
                  
                  return (
                    <div
                      key={d.value}
                      className={`date-pill ${isSelected ? "selected" : ""}`}
                      onClick={() => handleDropoffDatePill(d.value)}
                    >
                      <div className="date-pill-label">
                        {d.value === "custom" && dropoffCustomLabel 
                          ? dropoffCustomLabel
                          : d.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Dropoff Calendar */}
              {showDropoffCalendar && (
                <Calendar
                  onSelect={handleDropoffCalendarSelect}
                  onClose={() => setShowDropoffCalendar(false)}
                  minDate={bookingWindowStart}
                  maxDate={bookingWindowEnd}
                  selectedDate={dropoffDate}
                />
              )}

              {/* Dropoff Time Selection */}
              <div className="section-label">
                {isPodLockerType ? t('start_time_label') : t('dropoff_time_label')}
              </div>
              <div className="time-list-container">
                <div className="time-list">
                  {dropoffTimeSlots.map((t) => (
                    <button
                      key={t}
                      className={`time-slot ${dropoffTime === t ? "selected" : ""}`}
                      onClick={() => setDropoffTime(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Pickup Section */}
          {activeSection === "pickup" && (
            <>
              <div className="section-label">{isPodLockerType ? t('end_date_label') : t('pickup_date_label')}</div>
              <div className="date-row">
                {DATE_TIME_SELECTOR_DATE_OPTIONS.map((d) => {
                  let isSelected = false;
                  
                  if (d.value === "today") {
                    isSelected = isToday(pickupDate);
                  } else if (d.value === "tomorrow") {
                    isSelected = isTomorrow(pickupDate);
                  } else if (d.value === "custom") {
                    isSelected = pickupCustomLabel !== null && !isToday(pickupDate) && !isTomorrow(pickupDate);
                  }

                  const isDisabled = isPickupPillDisabled(d.value);

                  return (
                    <div
                      key={d.value}
                      className={`date-pill ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                      onClick={() => !isDisabled && handlePickupDatePill(d.value)}
                    >
                      <div className="date-pill-label">
                        {d.value === "custom" && pickupCustomLabel
                          ? pickupCustomLabel
                          : d.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pickup Calendar */}
              {showPickupCalendar && (
                <Calendar
                  onSelect={handlePickupCalendarSelect}
                  onClose={() => setShowPickupCalendar(false)}
                  minDate={bookingWindowStart}
                  maxDate={pickupWindowEnd}
                  selectedDate={pickupDate}
                />
              )}

              {/* Pickup Time Selection */}
              <div className="section-label">
                {isPodLockerType ? t('end_time_label') : t('pickup_time_label')}
              </div>
              <div className="time-list-container">
                <div className="time-list">
                  {pickupTimeSlots.map((t) => (
                    <button
                      key={t}
                      className={`time-slot ${pickupTime === t ? "selected" : ""}`}
                      onClick={() => setPickupTime(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom Area */}
        <div className="bottom-area">
          <button
            className="continue-btn"
            onClick={handleContinueClick}
            disabled={isSubmitting}
          >
            {t('button_continue')}
          </button>
        </div>
      </div>
      
    {confirmationModel && (
      <CustomDialog
        dialogId='confirmation-dialog'
        dialogVisible={confirmationModel}
        dialogTitle={t('warning_msg_confirm_your_booking_date_time')}
        buttonOneTitle={t('button_cancel')}
        buttonTwoTitle={t('button_ok')}
        buttonProps={{
          buttonOne: { type: BUTTON_WARNING },
          buttonTwo: { type: BUTTON_PRIMARY }
        }}
        handleAccept={handleConfirmBooking}
        handleCancel={handleCancelBooking}
        onClose={handleCancelBooking}
      >
        {renderConfirmationContent}
      </CustomDialog>
)}
    </div>
  );
};

export default DateTimeSelector;
